/**
 * Status language – single source of tone per `domain.status` (HANDOFF §2.2).
 * Labels and explanations come from i18n: `status.<domain>.<status>.admin | .portal | .help`.
 * `portal` may be absent (status never shown in the portal).
 */
export type Tone = "ok" | "info" | "warn" | "bad" | "neutral";
export type StatusDomain =
  | "identity" | "request" | "step" | "account" | "syncRun"
  | "sod" | "certification" | "remediation" | "agent" | "role" | "assignment" | "job" | "campaign"
  // Tenant admin settings and operations
  | "user" | "apiKey" | "enabled" | "lifecycle" | "circuit" | "watch" | "relay"
  // Hosting (control plane) domains
  | "tenant" | "tenantRequest" | "provisioningJob" | "release" | "upgrade" | "upgradeOutcome" | "cluster" | "domain" | "platformUser";

export type StatusKey = `${StatusDomain}.${string}`;

export const statusTone: Record<StatusKey, Tone> = {
  "identity.pre_hire": "neutral", "identity.active": "ok", "identity.leave": "neutral",
  "identity.offboarding": "info", "identity.inactive": "neutral",

  "request.submitted": "info", "request.pending_approval": "info", "request.approved": "ok",
  "request.rejected": "neutral", "request.completed": "ok", "request.failed": "bad",
  "request.cancelled": "neutral",

  "step.waiting": "neutral", "step.pending": "warn", "step.skipped": "neutral",
  "step.escalated": "warn", "step.approved": "ok", "step.rejected": "neutral",

  "account.present": "ok", "account.pending_add": "info", "account.pending_remove": "info",
  "account.orphan": "warn", "account.external": "neutral", "account.disabled": "neutral",
  "account.locked": "warn", "account.deleted": "neutral",

  "syncRun.queued": "info", "syncRun.running": "info", "syncRun.awaiting_review": "warn",
  "syncRun.held": "warn", "syncRun.succeeded": "ok", "syncRun.failed": "bad",
  "syncRun.cancelled": "neutral", "syncRun.dry_run": "neutral",

  "sod.blocked": "bad", "sod.open": "warn", "sod.mitigated": "neutral", "sod.resolved": "ok",

  "certification.undecided": "warn", "certification.keep": "ok",
  "certification.revoke": "neutral", "certification.reassign": "neutral",
  "remediation.pending": "info", "remediation.done": "ok", "remediation.failed": "bad",

  "agent.online": "ok", "agent.offline": "bad", "agent.disabled": "neutral",
  "role.draft": "neutral", "role.published": "ok", "role.retired": "neutral",
  "assignment.active": "ok", "assignment.pending": "info", "assignment.expiring": "warn",
  "assignment.expired": "neutral", "assignment.revoked": "neutral",

  /* Background jobs (worker queue). `quarantined` = failed repeatedly and set aside until an admin resumes it. */
  "job.queued": "info", "job.running": "info", "job.failed": "bad", "job.quarantined": "bad",
  "job.cancelled": "neutral", "job.succeeded": "ok",

  /* Certification campaigns. */
  "campaign.draft": "neutral", "campaign.active": "info", "campaign.completed": "ok", "campaign.cancelled": "neutral",

  /* Tenant admin users, API keys and on/off switches (SSO providers, webhooks). */
  "user.active": "ok", "user.invited": "info", "user.disabled": "neutral",
  "apiKey.active": "ok", "apiKey.expired": "neutral", "apiKey.revoked": "neutral",
  "enabled.enabled": "ok", "enabled.disabled": "neutral",
  /* Lifecycle events waiting for the worker. */
  "lifecycle.waiting": "info", "lifecycle.processed": "ok",
  /* Circuit breaker of a realtime/delta source. */
  "circuit.closed": "ok", "circuit.half_open": "warn", "circuit.open": "bad",
  /* Agent change-watch per domain. */
  "watch.watching": "ok", "watch.starting": "warn", "watch.catching-up": "warn", "watch.reconnecting": "warn", "watch.paused": "warn",
  "watch.error": "bad", "watch.stopped": "neutral", "watch.unknown": "neutral",
  /* Requests relayed through an agent. */
  "relay.queued": "info", "relay.running": "info", "relay.done": "ok", "relay.failed": "bad",

  /* ---- Hosting (control plane) domains ---- */
  // Tenant catalogue row. Red is reserved for "stuck"; suspended is deliberate and needs a person to lift it.
  "tenant.requested": "neutral", "tenant.provisioning": "info", "tenant.active": "ok", "tenant.suspended": "warn", "tenant.deprovisioning": "info", "tenant.deleted": "neutral",
  // Self-service sign-ups: pending = an operator must decide.
  "tenantRequest.email_verification": "neutral", "tenantRequest.pending": "warn", "tenantRequest.approved": "info", "tenantRequest.rejected": "neutral", "tenantRequest.provisioned": "ok",
  // Control-plane jobs (provision, migrate, deprovision, upgrade, move_cluster).
  "provisioningJob.queued": "info", "provisioningJob.running": "info", "provisioningJob.succeeded": "ok", "provisioningJob.failed": "bad",
  // Platform and agent releases.
  "release.draft": "neutral", "release.available": "ok", "release.closed": "neutral",
  // A tenant's position against the newest release.
  "upgrade.up_to_date": "ok", "upgrade.available": "info", "upgrade.scheduled": "info", "upgrade.running": "info", "upgrade.failed": "bad", "upgrade.forced": "warn", "upgrade.blocked": "bad",
  "upgrade.downloading": "info", "upgrade.installing": "info",
  // Rows of tenant_upgrade_history.
  "upgradeOutcome.running": "info", "upgradeOutcome.succeeded": "ok", "upgradeOutcome.failed": "bad", "upgradeOutcome.rolled_back": "bad",
  // Cluster health check result (client side).
  "cluster.unchecked": "neutral", "cluster.checking": "info", "cluster.ok": "ok", "cluster.down": "bad", "cluster.full": "warn",
  // Custom domains.
  "domain.verified": "ok", "domain.pending": "info",
  // Platform staff.
  "platformUser.active": "ok", "platformUser.must_change_password": "warn", "platformUser.disabled": "neutral",
};

export const STATUS_DOMAINS: StatusDomain[] = [
  "identity", "request", "step", "account", "syncRun", "sod", "certification", "remediation", "agent", "role", "assignment", "job", "campaign",
  "user", "apiKey", "enabled", "lifecycle", "circuit", "watch", "relay",
  "tenant", "tenantRequest", "provisioningJob", "release", "upgrade", "upgradeOutcome", "cluster", "domain", "platformUser",
];

/** Default list order: Blocked → Needs attention → the rest. */
export const toneRank: Record<Tone, number> = { bad: 0, warn: 1, info: 2, ok: 3, neutral: 4 };

/** Tone name as shown in the design system (ds-status board). */
export const toneLabel: Record<Tone, string> = { ok: "Healthy", info: "In progress", warn: "Needs attention", bad: "Blocked", neutral: "Neutral" };

const warned = new Set<string>();
function warnOnce(msg: string) {
  if (warned.has(msg)) return;
  warned.add(msg);
  const dev = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV ?? true;
  if (dev && typeof console !== "undefined") console.warn(`[iga/ui] ${msg}`);
}

/** Tone for a domain + status; unknown keys are neutral and warn once in dev mode. */
export function toneOf(domain: StatusDomain, status: string | null | undefined): Tone {
  const key = `${domain}.${status ?? ""}` as StatusKey;
  const tone = statusTone[key];
  if (!tone) { warnOnce(`Unknown status "${key}" – add it to statusMap.ts and the i18n files.`); return "neutral"; }
  return tone;
}

export function isKnownStatus(domain: StatusDomain, status: string | null | undefined): boolean {
  return `${domain}.${status ?? ""}` in statusTone;
}

/** "awaiting_review" → "Awaiting review" (used only as the fallback label for unknown keys). */
export function humaniseKey(status: string | null | undefined): string {
  const s = (status ?? "").replaceAll(/[_-]+/g, " ").trim();
  return s ? s[0]!.toUpperCase() + s.slice(1) : "–";
}

/** All statuses of a domain (for filters, the style guide and tests). */
export function statusesOf(domain: StatusDomain): string[] {
  return Object.keys(statusTone).filter((k) => k.startsWith(`${domain}.`)).map((k) => k.slice(domain.length + 1));
}

/** Best-effort domain guess for the deprecated `StatusBadge({ status })` signature. First domain that knows the key wins. */
const GUESS_ORDER: StatusDomain[] = ["request", "identity", "syncRun", "account", "step", "sod", "certification", "remediation", "agent", "role", "assignment", "job", "campaign"];
export function guessDomain(status: string | null | undefined): StatusDomain | null {
  for (const d of GUESS_ORDER) if (isKnownStatus(d, status)) return d;
  return null;
}
